import { useEffect, useMemo, useState } from 'react'
import {
  averageProfitMarginForProjectsStartedSince,
  PROJECT_LABOUR_RATE_PER_HOUR,
  type AverageProfitMarginResult,
} from '../components/ProfitabilityPerformance/mockData'
import { EXECUTIVE_SUMMARY_PROFIT_MARGIN_START_DATE } from '../data/executiveSummaryData'
import { fetchProjectProfitabilityFromFirestore } from '../services/pwfProfitabilitySnapshotService'

export function useExecutiveSummaryProfitMargin() {
  const [recordedAt, setRecordedAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<AverageProfitMarginResult>({
    marginPct: null,
    projectCount: 0,
    invoicedTotal: 0,
  })

  useEffect(() => {
    let cancelled = false

    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const snapshot = await fetchProjectProfitabilityFromFirestore()
        if (cancelled) return
        if (!snapshot) {
          setRecordedAt(null)
          setResult({ marginPct: null, projectCount: 0, invoicedTotal: 0 })
          return
        }
        setRecordedAt(snapshot.recordedAt)
        setResult(
          averageProfitMarginForProjectsStartedSince(
            snapshot.projectProfitability,
            EXECUTIVE_SUMMARY_PROFIT_MARGIN_START_DATE,
            PROJECT_LABOUR_RATE_PER_HOUR
          )
        )
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load profit margin')
          setResult({ marginPct: null, projectCount: 0, invoicedTotal: 0 })
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  const profitMarginPct = useMemo(
    () => (result.marginPct != null ? Math.round(result.marginPct * 10) / 10 : null),
    [result.marginPct]
  )

  return {
    profitMarginPct,
    projectCount: result.projectCount,
    invoicedTotal: result.invoicedTotal,
    startDateFrom: EXECUTIVE_SUMMARY_PROFIT_MARGIN_START_DATE,
    recordedAt,
    loading,
    error,
  }
}
